import { apiRequest } from '@lib/api';

import { Component, ComponentListResponse } from '../schemas/component';

/**
 * The component catalog API, one function per route.
 *
 * Two routes today, and they sit at different tiers server-side: listing is
 * `read-only` because the catalog names capabilities rather than customers,
 * while creating is `engineer`. The tier is not enforced here — the API decides
 * — but the control that calls {@link createComponent} must gate on the same
 * one, or the UI offers a button that was always going to be refused.
 */

export async function listComponents(signal?: AbortSignal): Promise<Component[]> {
    const { components } = await apiRequest('/components', ComponentListResponse, { signal });
    return components;
}

export interface CreateComponentInput {
    /** Permanent. Grants are written under it, and nothing renames or reuses one. */
    readonly id: string;
    readonly displayName: string;
    readonly description: string;
    readonly owner: string;
    /**
     * Members. Every one must be a flag whose `purpose` is `entitlement`; the
     * API refuses the rest with `COMPONENT_MEMBER_NOT_ENTITLEMENT`, naming the
     * flag. May be empty — a bundle can be assembled before its flags exist.
     */
    readonly flagKeys: readonly string[];
    readonly ownerService?: string;
    readonly activationPath?: string;
    readonly reason?: string;
}

/**
 * Define a component. Grants nothing: no museum gains a flag, and the row holds
 * no activation state — which is what lets an operator assemble a bundle before
 * deciding who gets it.
 */
export function createComponent(input: CreateComponentInput): Promise<Component> {
    return apiRequest('/components', Component, { method: 'POST', body: input });
}
